import type { ApiError, LicenseState } from "@clubcore/api";
import { LICENSE_READ_ONLY, MODULE_NOT_LICENSED } from "@clubcore/api";

export interface LicenseSnapshot {
  /** Last state the server told us about; null until a response says (null is treated as valid). */
  status: LicenseState | null;
  /** Backend modules that answered `module_not_licensed`. */
  blockedModules: readonly string[];
}

const KNOWN: readonly string[] = ["valid", "grace", "expired", "revoked", "invalid", "suspended", "unlicensed"];

/**
 * What the server has told this browser about the license, learned passively from every response:
 * the `X-ClubCore-License-Status` header (sent whenever the state is not "valid"), a `license_read_only`
 * error, and `module_not_licensed` errors. Nothing here needs a permission.
 */
export class LicenseStore {
  private snapshot: LicenseSnapshot = { status: null, blockedModules: [] };
  private listeners = new Set<() => void>();

  getSnapshot = (): LicenseSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /** Plug into the API client's `onResponse`. */
  observe = ({ status, headers, error }: { status: number; headers: Headers; error?: ApiError | undefined }): void => {
    if (error?.code === LICENSE_READ_ONLY) {
      const reported = error.details.license_status;
      this.set({ status: typeof reported === "string" && KNOWN.includes(reported) ? (reported as LicenseState) : "expired" });
      return;
    }
    if (error?.code === MODULE_NOT_LICENSED) {
      const module = error.details.module;
      if (typeof module === "string" && !this.snapshot.blockedModules.includes(module)) {
        this.set({ blockedModules: [...this.snapshot.blockedModules, module] });
      }
      return;
    }
    if (status >= 500 || status === 0) return;
    const header = headers.get("X-ClubCore-License-Status");
    if (header && KNOWN.includes(header)) this.set({ status: header as LicenseState });
    else if (!error && this.snapshot.status !== null) this.set({ status: "valid" }); // no header on a module route means valid
  };

  /** The status page knows better than the passive signals. */
  report(status: LicenseState): void {
    this.set({ status });
  }

  reset(): void {
    this.snapshot = { status: null, blockedModules: [] };
    this.emit();
  }

  private set(change: Partial<LicenseSnapshot>): void {
    const next = { ...this.snapshot, ...change };
    if (next.status === this.snapshot.status && next.blockedModules === this.snapshot.blockedModules) return;
    this.snapshot = next;
    this.emit();
  }

  private emit() {
    for (const listener of [...this.listeners]) listener();
  }
}

/** Reads and changes are both fine in these states; writes are refused in every other. */
export function isWritable(status: LicenseState | null): boolean {
  return status === null || status === "valid" || status === "grace";
}
